import {
  faBracketsCurly,
  faUpRightFromSquare,
} from "@fortawesome/pro-regular-svg-icons";
import React from "react";
import { twJoin, twMerge } from "tailwind-merge";

import { AgentResponseMapping } from "src/agentNode/components/AgentResponseMapping";
import { SchemaError } from "src/agentNode/types";
import { Button } from "src/base-components/Button";
import { Card } from "src/base-components/Card";
import { Icon } from "src/base-components/Icon";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import {
  SCHEMA_MAPPING_ARROW_WIDTH,
  SchemaProperty,
} from "src/parentFlowNodes/SchemaProperty";
import { useAuthoringContext } from "src/router/routerContextHooks";
import { getBaseUrl, getUrlToAgentVersionPage } from "src/router/urls";
import { SchemaUI } from "src/schema/schemaMappingUtils";

type Props = {
  inputSchema: SchemaUI | undefined;
  agentName: string;
  agentId: string;
  versionId: string;
  immutable: boolean;
  isLoading: boolean;
  inputMappingErrors: Record<string, SchemaError>;
};

export const AgentInputOutputMappings: React.FC<Props> = ({
  inputSchema,
  agentName,
  agentId,
  versionId,
  immutable,
  isLoading,
  inputMappingErrors,
}) => {
  if (isLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="flex flex-col gap-y-4">
      {/* Input Mappings */}
      <AgentDataMap
        agentId={agentId}
        agentName={agentName}
        fieldMappingErrors={inputMappingErrors}
        readonly={immutable}
        schema={inputSchema}
        title="Inputs"
        versionId={versionId}
      />

      {/* Response Mapping */}
      <AgentResponseMapping immutable={immutable} />
    </div>
  );
};

type AgentDataMapProps = {
  title: string;
  schema: SchemaUI | undefined;
  agentName: string;
  agentId: string;
  versionId: string;
  readonly: boolean;
  fieldMappingErrors: Record<string, SchemaError>;
};

const AgentDataMap: React.FC<AgentDataMapProps> = ({
  title,
  schema,
  agentName,
  agentId,
  versionId,
  readonly,
  fieldMappingErrors,
}) => {
  const hasSchema = schema && schema.properties.length > 0;
  const hasErrors = Object.keys(fieldMappingErrors).length > 0;

  return (
    <div className="flex flex-col gap-y-2">
      <span className="text-gray-800 font-inter-semibold-13px">{title}</span>
      <Card variant={hasSchema ? "default" : "inverted"}>
        <Card.Content>
          {!hasSchema ? (
            <MissingAgentSchema
              agentId={agentId}
              agentName={agentName}
              versionId={versionId}
            />
          ) : (
            <div className="flex flex-col gap-y-2">
              <div className={twMerge("mb-1 flex w-full gap-x-3")}>
                <p className="w-1/2 text-gray-500 font-inter-normal-12px">
                  Current Decision Flow
                </p>
                <div className={SCHEMA_MAPPING_ARROW_WIDTH} />
                <p className="w-1/2 text-gray-500 font-inter-normal-12px">
                  {agentName}
                </p>
              </div>
              {schema.properties.map((field) => (
                <SchemaProperty
                  key={field.fieldName}
                  field={field}
                  fieldError={Boolean(fieldMappingErrors[field.fieldName])}
                  highlighted={false}
                  isLoopNode={false}
                  isOutput={false}
                  lazy={false}
                  readonly={readonly}
                  schemaTabUrl=""
                  shouldDisplayError={hasErrors}
                />
              ))}
            </div>
          )}
        </Card.Content>
      </Card>
    </div>
  );
};

const LoadingSkeleton: React.FC = () => (
  <div className="flex flex-col gap-y-4">
    <div className="flex flex-col gap-y-2">
      <SkeletonPlaceholder height="h-4" width="w-16" />
      <Card>
        <Card.Content>
          <div className="flex flex-col gap-y-2">
            <SkeletonPlaceholder height="h-8" />
            <SkeletonPlaceholder height="h-8" />
          </div>
        </Card.Content>
      </Card>
    </div>
    <div className="flex flex-col gap-y-2">
      <SkeletonPlaceholder height="h-4" width="w-16" />
      <Card>
        <Card.Content>
          <SkeletonPlaceholder height="h-8" />
        </Card.Content>
      </Card>
    </div>
  </div>
);

type MissingAgentSchemaProps = {
  agentName: string;
  agentId: string;
  versionId: string;
};

const MissingAgentSchema: React.FC<MissingAgentSchemaProps> = ({
  agentName,
  agentId,
  versionId,
}) => {
  const { orgId, workspace } = useAuthoringContext();

  const url = `${getBaseUrl()}${getUrlToAgentVersionPage(
    orgId,
    workspace.id,
    agentId,
    versionId,
  )}`;

  return (
    <div className="flex flex-col items-center px-16 py-12">
      <div className={twJoin("mb-4 rounded-lg bg-orange-100 p-4")}>
        <Icon color="text-orange-600" icon={faBracketsCurly} size="md" />
      </div>
      <p className="mb-2 text-gray-800 font-inter-semibold-13px">
        Missing Schema
      </p>
      <p className="mb-6 text-center text-gray-500 font-inter-normal-12px">
        Define an input schema for{" "}
        <span className="font-inter-medium-12px">{agentName}</span>
        <br />
        to complete mapping.
      </p>
      <a href={url} rel="noreferrer" target="_blank">
        <Button iconLeft={faUpRightFromSquare} size="base" variant="secondary">
          Define Schema
        </Button>
      </a>
    </div>
  );
};
